'use client';

import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Loader2, ScanText, X } from 'lucide-react';

import { Link, useRouter } from '@/i18n/routing';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OcrResultsPage } from '@/features/ocr';
import { QrReviewDetailPage } from '@/features/qr-review';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { ocrQaQueries } from '../services/ocr-qa.queries';
import {
  useStartOcrQaMutation,
  useUpdateOcrQaFieldMutation
} from '../services/ocr-qa.mutations';
import { OcrQaVerdictDialog } from './ocr-qa-verdict-dialog';
import { OcrQaRejectionComments } from './ocr-qa-rejection-comments';
import { OcrQaPageVerdictButtons } from './ocr-qa-page-verdict-buttons';
import { OCR_QA_HASH, OCR_QA_ACTIVE_STAGES } from '../constants';
import type { OcrQaTier, OcrQaResult, OcrQaVerdict } from '../types';

interface Props {
  tier: OcrQaTier;
  containerId: number;
}

const TIER_LIST_ROUTE: Record<OcrQaTier, string> = {
  1: '/dashboard/qa-ocr-1',
  2: '/dashboard/qa-ocr-2',
  3: '/dashboard/qa-ocr-3'
};

// Container-level accept/reject — rendered in the right panel footer. The ONLY
// control that moves the container: a per-page verdict just records what the
// reviewer found. Applies to every actionable page as one unit via the
// container-verdict endpoint; the reject dialog collects the per-page notes.
function ContainerVerdictButtons({
  tier,
  containerId,
  actionableResults
}: {
  tier: OcrQaTier;
  containerId: number;
  actionableResults: OcrQaResult[];
}) {
  const t = useTranslations('ocrQa');
  const router = useRouter();
  // Verdict confirmation lives in the URL hash
  // (#verdict-container/<accepted|rejected>) so it is deep-linkable and closes
  // on browser Back. The verdict kind rides as the hash param.
  const {
    open: verdictOpen,
    param: verdictParam,
    openWith: openVerdict,
    onOpenChange: closeVerdict
  } = useHashParamDialog(OCR_QA_HASH.verdictContainer);
  const pendingVerdict = verdictOpen
    ? ((verdictParam as OcrQaVerdict | null) ?? null)
    : null;

  if (actionableResults.length === 0) return null;

  // Accepting a container the reviewer has already faulted a page on would
  // forward work they said was wrong, and strand a `rejected` page row on a
  // container that moved on. The backend refuses it too; this is so the button
  // does not look available. Changing that page's verdict clears the block.
  const rejectedPageCount = actionableResults.reduce(
    (total, result) =>
      total +
      (result.qa_page_reviews ?? []).filter(
        (r) => r.tier === tier && r.verdict === 'rejected' && !r.superseded_at
      ).length,
    0
  );

  return (
    <>
      <div className='flex gap-2 border-t px-5 py-4'>
        <Button
          size='sm'
          variant='destructive'
          className='flex-1'
          onClick={() => openVerdict('rejected')}
        >
          <X className='me-1.5 h-3.5 w-3.5' />
          {t('rejectContainer')}
        </Button>
        <Button
          size='sm'
          className='flex-1'
          disabled={rejectedPageCount > 0}
          title={
            rejectedPageCount > 0
              ? t('acceptBlockedByRejectedPages', { count: rejectedPageCount })
              : undefined
          }
          onClick={() => openVerdict('accepted')}
        >
          <Check className='me-1.5 h-3.5 w-3.5' />
          {t('acceptContainer')}
        </Button>
      </div>
      {rejectedPageCount > 0 && (
        <p className='px-5 pb-3 text-xs text-amber-600 dark:text-amber-400'>
          {t('acceptBlockedByRejectedPages', { count: rejectedPageCount })}
        </p>
      )}

      <OcrQaVerdictDialog
        tier={tier}
        containerId={containerId}
        verdict={pendingVerdict}
        actionableResults={actionableResults}
        onClose={() => closeVerdict(false)}
        onSuccess={() => router.push(TIER_LIST_ROUTE[tier])}
      />
    </>
  );
}

export function OcrQaDetailPage({ tier, containerId }: Props) {
  const t = useTranslations('ocrQa');
  const { data, isLoading } = useQuery(
    ocrQaQueries.containerResults(tier, containerId)
  );
  const startMutation = useStartOcrQaMutation(tier, { silent: true });
  const qaFieldMutation = useUpdateOcrQaFieldMutation(tier);
  const hasAutoClaimedRef = useRef(false);

  const results = useMemo(() => data?.data ?? [], [data]);

  // Auto-claim pending pages on first load
  useEffect(() => {
    if (!results.length || hasAutoClaimedRef.current) return;
    hasAutoClaimedRef.current = true;

    for (const r of results) {
      if (r.qa_stage === `pending_qa${tier}`) {
        startMutation.mutate(r.id);
      }
    }
  }, [results, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Progress stats ──
  const actionableResults = useMemo(
    () =>
      results.filter((r) =>
        OCR_QA_ACTIVE_STAGES[tier].includes(r.qa_stage ?? '')
      ),
    [results, tier]
  );
  const doneCount = results.length - actionableResults.length;
  // The whole container moves as a unit — verified fields are editable only
  // while the container is actionable at this tier.
  const canEditVerified = actionableResults.length > 0;

  // QA-scoped field edit: routes through the tier's own field endpoint so a
  // reviewer without `ocr.edit` can still correct a value.
  const handleQaUpdateField = useCallback(
    async (
      resultId: number,
      key: string,
      value: string,
      pageNumber?: number | null
    ) => {
      try {
        await qaFieldMutation.mutateAsync({
          resultId,
          key,
          value,
          pageNumber,
          containerId
        });
      } catch {
        // handled by the global MutationCache
      }
    },
    [qaFieldMutation, containerId]
  );

  // ── Loading ──
  if (isLoading) {
    return (
      <div className='flex h-[85svh] items-center justify-center'>
        <Loader2 className='text-muted-foreground h-8 w-8 animate-spin' />
      </div>
    );
  }

  if (!results.length) {
    return (
      <div className='flex h-[85svh] flex-col items-center justify-center gap-3'>
        <ScanText className='text-muted-foreground/30 h-12 w-12' />
        <p className='text-muted-foreground'>{t('emptyState')}</p>
      </div>
    );
  }

  // ── Scanning tab — derive batchId from OCR results ──
  const scanningBatchId = results[0]?.batch_id ?? null;

  return (
    <div className='space-y-4'>
      {/* Top bar */}
      <div className='flex items-center justify-between'>
        <Button asChild variant='ghost' size='sm'>
          <Link href={TIER_LIST_ROUTE[tier]}>
            <ArrowLeft className='me-2 h-4 w-4' />
            {t('backToList')}
          </Link>
        </Button>
        <Badge variant='outline'>
          {doneCount}/{results.length} {t('metricCompletedToday').toLowerCase()}
        </Badge>
      </div>

      <Tabs defaultValue='ocr' className='space-y-4'>
        <TabsList>
          <TabsTrigger value='ocr'>{t('tabOcr')}</TabsTrigger>
          {tier === 3 && (
            <TabsTrigger value='scanning'>{t('tabScanning')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value='ocr' className='mt-0'>
          <OcrResultsPage
            containerId={containerId}
            surface='qa'
            // The panel does not know which tier is rendering it, so it cannot
            // work out whether the container is still ours to correct. This is
            // the same set the verdict buttons act on.
            qaTierActive={actionableResults.length > 0}
            allowEditVerified={canEditVerified}
            onUpdateFieldOverride={handleQaUpdateField}
            enableBboxEditing={false}
            renderPageFooter={(result, pageIndexInResult, isFirstGroup) => {
              // Every page gets its own verdict, so this renders on EVERY page
              // section — not once per result. It used to render only on a
              // result's first section because the verdict was result-scoped,
              // which is why the buttons appeared on an apparently arbitrary
              // handful of pages.
              //
              // Except the unattributed section (`pageIndexInResult === 0`),
              // which addresses no page and must never be passed to a per-page
              // endpoint as if it were one.
              if (pageIndexInResult === 0) return null;
              return (
                <>
                  <OcrQaPageVerdictButtons
                    tier={tier}
                    containerId={containerId}
                    result={result}
                    pageNumber={pageIndexInResult}
                    pageLabel={
                      result.pages?.[pageIndexInResult - 1]?.page_number ?? null
                    }
                  />
                  <OcrQaRejectionComments
                    result={result}
                    pageNumber={pageIndexInResult}
                    includeSharedNotes={isFirstGroup}
                  />
                </>
              );
            }}
            renderPanelFooter={() => (
              <ContainerVerdictButtons
                tier={tier}
                containerId={containerId}
                actionableResults={actionableResults}
              />
            )}
          />
        </TabsContent>

        {tier === 3 && (
          <TabsContent value='scanning' className='mt-0'>
            <div className='bg-background overflow-hidden rounded-xl border shadow-sm'>
              {scanningBatchId ? (
                <QrReviewDetailPage batchId={scanningBatchId} readOnly />
              ) : (
                <div className='flex h-[85svh] flex-col items-center justify-center gap-3'>
                  <ScanText className='text-muted-foreground/30 h-12 w-12' />
                  <p className='text-muted-foreground'>{t('emptyState')}</p>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
